
import ServiceBase from "./service-base";

// this allows us to swap out the library with the new version
import { Font, FontLoader } from "three/examples/jsm/loaders/FontLoader";
import { TextGeometry, TextGeometryParameters } from "three/examples/jsm/geometries/TextGeometry";


type TextGeometrySettings = {
  size?: number;
  depth?: number;  // replaces height which is deprecated
  curveSegments?: number;
  bevelEnabled?: boolean;
  bevelThickness?: number;
  bevelSize?: number;
  bevelOffset?: number;
  bevelSegments?: number;
  font?: string;
};

/**
 * @category Services
 */
export class ThreeFontService extends ServiceBase {
  protected fonts: { [fontName: string]: any } = {};
  protected _name = "ThreeFontService";
  protected _defaultFontMeta =
    "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json";
  // protected _defaultFontMeta = "helvetiker_regular.typeface.json";
  protected _defaultFontName = "helvetiker_regular";

  // this is where we set up any loading stuff
  public registerLoadPromises(
    defaultFontMetaJsonFile: string = this._defaultFontMeta,
    defaultFontName: string = this._defaultFontName
  ) {
    // make sure our prerequisites are loaded up
    this._supervisor.addLoadPromise(
      new Promise((resolve, reject) => {
        this._loadFont(
          defaultFontName,
          defaultFontMetaJsonFile,
          (font: Font) => {
            resolve();
          }
        );
      })
    );
  }

  // load up a font
  protected _loadFont(
    fontName: string,
    metaFileName: string,
    callback: (font: Font) => void
  ) {
    // dont load the same font twice
    if (this.fonts[fontName]) return;

    // set them to true while they're loading
    this.fonts[fontName] = true;
    let isDefault = false;
    if (!this.fonts["default"]) {
      isDefault = true;
      this.fonts["default"] = true;
    }

    // set off the load process
    const fontLoader = new FontLoader();
    fontLoader.load(metaFileName, (font) => {
      // store the font
      this.fonts[fontName] = font;
      if (isDefault) {
        this.fonts["default"] = font;
      }
      if (callback) callback(font);
    });
  }

  // returns true if the font has been loaded
  public fontLoaded(fontName: string): boolean {
    if (!this.fonts[fontName] || this.fonts[fontName] === true) {
      return false;
    }
    return true;
  }

  public createFontGeometry(
    text: string,
    settings?: TextGeometrySettings
  ): TextGeometry {
    if (!settings) settings = this.getGeometryConfigObject();
    let fontName = settings.font;

    if (!this.fonts[fontName]) {
      throw new Error(`The font ${fontName} has not been loaded`);
    }

    // create the mesh
    if (this.fonts[fontName] === true) return;

    // create the final config
    const defaultConfig = this.getGeometryConfigObject();

    let configAssembly: TextGeometrySettings = { ...defaultConfig };
    if (settings) {
      configAssembly = { ...configAssembly, ...settings };
    }
    const finalConfig: TextGeometryParameters = {
      ...configAssembly,
      font: this.fonts[configAssembly.font],
    };

    const geometry = new TextGeometry(text, finalConfig);
    return geometry;
  }

  public getGeometryConfigObject(): TextGeometrySettings {
    return {
      size: 80,
      depth: 1, // replaces height which is deprecated
      curveSegments: 12,
      bevelEnabled: false,
      bevelThickness: 10,
      bevelSize: 8,
      bevelOffset: 0,
      bevelSegments: 5,
      font: "default",
    };
  }
}
export default ThreeFontService;
